import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers, {
  StatisticsProps,
} from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import {
  COURSE_FIRST_FOLD,
  COURSE_NAMES,
  FAQ,
} from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import ExpandableActivities from '@components/course-landing-page/expandable-activities'
import CourseQuizzes from '@components/course-landing-page/quizzes'
import ExploreTools, {
  ToolCardData,
} from '@components/course-landing-page/explore-tools'
import CodingalSubscription from '@components/home/codingal-subscription'
import CodingalProductBlog from '@components/blog/product-blogs'
import AboutUsCard from '@components/reviews-and-testimonials/about-us-card'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import HowClassWorks from '@components/course-landing-page/how-class-works'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { CODING_GRADE_9_10 } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['cgm'].trialCourseQuery}`

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1,000+',
    description: defineMessage({ message: 'Expert CS instructors' }),
    backgroundColor: 'bg-orange-200',
  },
  {
    title: '👨‍💻 7 years',
    description: defineMessage({ message: 'Average teaching experience' }),
    backgroundColor: 'bg-blue-50',
  },
  {
    title: '👩 91%',
    description: defineMessage({ message: 'Female faculty' }),
    backgroundColor: 'bg-yellow-100',
  },
  {
    title: '💻 500+',
    description: defineMessage({ message: 'Lessons and projects' }),
    backgroundColor: 'bg-green-50',
  },
]

const GRADE_9_10_TOOLS: ToolCardData[] = [
  {
    title: 'Python',
    description:
      'Write real Python code to build apps, tools, and solve problems like a developer.',
    image: '/images/courses/coding-for-kids/python.jpg',
  },
  {
    title: 'Data Science & ML',
    description:
      'Explore real datasets, spot patterns, and discover how data powers smart technology.',
    image: '/images/courses/coding-for-kids/data-science-and-ml.jpg',
  },
  {
    title: 'Website Development',
    description:
      'Build and design real websites using HTML, CSS, and beginner JavaScript.',
    image: '/images/courses/coding-for-kids/website.jpg',
  },
  {
    title: 'Machine Learning',
    description:
      'Build and train machine learning models through structured, hands-on projects.',
    image: '/images/courses/coding-for-kids/machine-learning.jpg',
  },
  {
    title: 'AI',
    description:
      'Design and build AI-powered apps and tools using real programming concepts.',
    image: '/images/courses/coding-for-kids/ai.jpg',
  },
  {
    title: 'Java',
    description:
      'Write and run Java code to build structured programs and solve complex problems.',
    image: '/images/courses/coding-for-kids/java.jpg',
  },
]

const CodingGrade910 = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }
  useStoreUTMParams()

  const seoConfig = formatSeoConfig({
    title: 'Online Coding Classes for Grades 9 & 10 (Ages 14-16) | LIVE 1-on-1',
    description:
      'Level up in coding & AI with LIVE 1-on-1 online classes for Grades 9 & 10. Build real-world projects and prep for competitive coding. Try a free lesson today!',
    url: 'https://www.codingal.com/coding-for-kids/grade-9-10/',
    metaImage:
      'https://www.codingal.com/images/courses/coding-for-kids/grade-9-10/og.jpg',
  })

  return (
    <>
      <NextSeo {...seoConfig} />
      <FAQPageJsonLd
        mainEntity={FAQ[CODING_GRADE_9_10].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName={COURSE_FIRST_FOLD[CODING_GRADE_9_10].name}
        description={COURSE_FIRST_FOLD[CODING_GRADE_9_10].description}
        provider={{ '@type': 'Organization', name: 'Codingal' }}
        offers={{ '@type': 'Offer', category: 'Paid' }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT20H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={CODING_GRADE_9_10}
        urlQuery={urlQuery}
        isPlatformPage
        hideReadMore
        background="bg-white"
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={CODING_GRADE_9_10} />
          <RecommendedCourses
            courseCodes={['cgm', 'cp', 'cc', 'wd', 'pc']}
            title="Best coding classes for teens aged 14 to 16"
            description={
              <>
                Tackle advanced Python, data science, AI, and competitive
                programming with expert live instruction.
              </>
            }
            showAllCoursesButton
          />
          <ExploreTools
            title="What do 14 to 16 year olds explore in Codingal coding classes?"
            description="Ninth and tenth graders work across advanced Python, data science, AI, and competitive programming through structured projects that reflect real technical ability. As early high schoolers develop their skills, they explore deeper pathways that align with their interests and future goals."
            urlQuery={urlQuery}
            tools={GRADE_9_10_TOOLS}
            note="⭐ As ninth and tenth graders advance, they may also explore competitive programming, advanced AI, and data science specializations."
          />
          <CourseFeatures
            courseTitle={CODING_GRADE_9_10}
            title={
              <Trans>
                An engaging coding journey for teens in Grades 9 and 10
              </Trans>
            }
          />
          <Activities courseName={CODING_GRADE_9_10} urlQuery={urlQuery} />
          <Teachers
            urlQuery={urlQuery}
            title={<Trans>Learn from expert Computer Science teachers</Trans>}
            statistics={STATISTICS}
            description={
              <Trans>
                Codingal&apos;s Grade 9 and 10 teachers are trained Computer
                Science educators with 7 or more years of classroom experience.
                Every instructor completes a structured onboarding program
                before teaching, and is regularly evaluated by student and
                parent ratings.
                <br />
                Teachers are rated 4.9 out of 5 by parents and students. The
                Codingal curriculum is rated 4.8 out of 5. The company Net
                Promoter Score (NPS) is 86 as of Q1 2026.
              </Trans>
            }
          />
          <HowClassWorks courseName={CODING_GRADE_9_10} urlQuery={urlQuery} />
          <CodingForStudents
            title="Our coding courses for Grades 9 and 10 are designed for students ready for a serious challenge"
            description="Our coding courses for Grades 9 and 10 are designed for students ready for a serious challenge. Ninth and tenth graders work across advanced Python, data science, AI, and competitive programming through structured live Codingal classes built around real technical depth. Early high schoolers tackle projects that reflect genuine coding ability and build portfolios that go well beyond the classroom. As 14 to 16 year olds advance through the curriculum, they develop professional-level problem-solving, strong data literacy, and the kind of independent thinking that opens doors to competitive colleges, tech internships, and meaningful career pathways."
            imagePath="/images/courses/coding-for-kids/grade-9-10/coding"
            imageWidth={213}
            imageHeight={219}
            reverse
          />
          <ExpandableActivities
            title="100+ coding projects your child will love building"
            description="A guided coding journey where students learn by creating, exploring, and building one step at a time."
            courseTitle={CODING_GRADE_9_10}
            urlQuery={urlQuery}
          />
          <CodingalSubscription compact subject="ELA" urlQuery={urlQuery} />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
            subject="coding"
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <StemAccredited
            urlQuery={urlQuery}
            heading="Unlock your skills and earn your advanced coding certificate"
            description="In Codingal's Grades 9 and 10 track, students progress through structured modules covering advanced Python, data science, AI, and competitive programming. Ninth and tenth graders learn everything from complex algorithms and data structures to machine learning concepts and real-world application development, building a portfolio that reflects serious technical ability. Upon completing the program, early high schoolers earn a STEM.org accredited certificate, a strong credential for college applications, tech internships, and academic portfolios."
          />
          <CodingCertificate
            title="Our coding classes are built for students ready for a serious challenge"
            description="At Codingal, our Grades 9 and 10 curriculum takes students from solid programming foundations into advanced, real-world development across multiple languages and disciplines. We begin with structured challenges that push logical thinking, problem-solving, and technical depth through hands-on project work. As ninth and tenth graders advance, they explore data science, AI, Java, and competitive programming at a level that mirrors real industry expectations. With every project they complete, early high schoolers build the kind of technical ability and independent thinking that opens doors to competitive colleges and meaningful tech careers."
            // imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our coding classes are built for students ready for a serious challenge"
            description="At Codingal, our Grades 9 and 10 curriculum takes students from solid programming foundations into advanced development across Python, data science, AI, and competitive programming. We begin with structured challenges that push logical thinking and technical depth through hands-on project work pitched at the right level for early high schoolers. As ninth and tenth graders advance, they tackle increasingly complex problems across multiple languages and disciplines that mirror real industry expectations. With every project they complete, students build the kind of technical ability and independent thinking that opens doors to competitive colleges and meaningful tech careers."
            imagePath="/images/courses/ai-and-ml/ai-course-students"
            imageWidth={235}
            imageHeight={241}
          />
          <Investors isCoursePage urlQuery={urlQuery} />
          <Faq
            courseName={CODING_GRADE_9_10}
            title="FAQs for Coding Classes for Grades 9 and 10"
          />
          <CourseQuizzes courseName={CODING_GRADE_9_10} />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            category="coding-for-kids"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={CODING_GRADE_9_10}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <AboutUsCard />
      <FooterBanner
        title="AI & coding classes for Grade 9-10 teens with the best AI & coding teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return { props: {} }
}

export default CodingGrade910
